import { useState } from 'react'
import { Link } from 'react-router-dom'
import type { Account } from '../../pages/admin/accountStore'

const FIELD = 'min-h-11 w-full rounded-control border border-line-strong px-2.5 text-small'
const LABEL = 'mb-1 block text-caption font-bold text-ink-muted'

/**
 * Gives an existing team a supervisor, or a different one. Only an active
 * supervisor account can lead a team. Changing a team's supervisor ends the
 * previous supervisor's access to its employees and records, so the form
 * names who that is before it saves.
 */
export default function AssignSupervisorForm({
  teamName,
  currentSupervisor,
  supervisors,
  saving,
  error,
  onSave,
  onCancel,
}: {
  teamName: string
  currentSupervisor: Account | null
  supervisors: Account[]
  saving: boolean
  error: string | null
  onSave: (supervisorId: string) => void
  onCancel: () => void
}) {
  const [supervisorId, setSupervisorId] = useState(
    supervisors.find((account) => account.id !== currentSupervisor?.id)?.id ?? supervisors[0]?.id ?? '',
  )

  const canSave = supervisorId !== '' && supervisorId !== currentSupervisor?.id

  return (
    <form
      className="flex flex-col gap-3 rounded-surface border border-line bg-canvas p-3"
      aria-label={`${currentSupervisor ? 'Change' : 'Assign'} the supervisor of ${teamName}`}
      onSubmit={(event) => {
        event.preventDefault()
        if (canSave && !saving) onSave(supervisorId)
      }}
    >
      {currentSupervisor && (
        <p className="text-small">
          {teamName} is led by <span className="font-bold">{currentSupervisor.firstName} {currentSupervisor.lastName}</span>. Changing it ends their access to this team&apos;s employees and records.
        </p>
      )}

      {supervisors.length === 0 ? (
        <p className="text-small text-ink-muted">
          No active supervisor account exists yet. Create one in <Link to="/admin/accounts" className="font-bold underline underline-offset-2">Accounts &amp; roles</Link>.
        </p>
      ) : (
        <div className="max-w-md">
          <label htmlFor="team-supervisor" className={LABEL}>
            Supervisor
          </label>
          <select id="team-supervisor" value={supervisorId} onChange={(event) => setSupervisorId(event.target.value)} className={FIELD}>
            {supervisors.map((account) => (
              <option key={account.id} value={account.id}>
                {account.firstName} {account.lastName} ({account.email})
              </option>
            ))}
          </select>
        </div>
      )}

      {error && (
        <p role="alert" className="text-caption font-semibold text-error">
          {error}
        </p>
      )}
      <div className="flex gap-2">
        <button
          type="submit"
          disabled={!canSave || saving}
          className="flex min-h-11 items-center justify-center rounded-control bg-primary px-4 text-small font-bold text-primary-ink hover:bg-primary-hover disabled:opacity-40"
        >
          {saving ? 'Saving…' : currentSupervisor ? 'Change supervisor' : 'Assign supervisor'}
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="flex min-h-11 items-center justify-center rounded-control px-4 text-small font-bold text-ink-muted hover:bg-surface-2"
        >
          Cancel
        </button>
      </div>
    </form>
  )
}
